import React from 'react'
import { Box, CircularProgress, Typography, alpha } from '@mui/material'
import type { Theme } from '@mui/material/styles'

import type { SkyLoadProgress } from '../../hooks/socialdash'

export interface LoadProgressOverlayProps {
  progress: SkyLoadProgress
}

/** Reserve room for the full log so the panel does not resize as lines arrive. */
const MAX_LINES = 3
const LINE_HEIGHT = 20

/**
 * The "we are busy, and getting through it" panel over a loading dashboard.
 *
 * ─── WHY AN OVERLAY OVER A PROGRESSIVE PAGE ─────────────────────────────────
 * The panels underneath still render independently, the moment each one's data
 * lands — that is not changed and must not be. What was missing was any signal
 * during the second or two before the whole page settles: individual requests
 * were completing quickly and none of that was visible, so a fast page read as
 * a stalled one. This narrates what is already happening.
 *
 * ⚠ PRESENTATIONAL ONLY, and that is this directory's rule rather than a
 * preference (see index.ts). It fetches nothing and subscribes to nothing; it
 * takes a `SkyLoadProgress` and draws it. The half that knows a query cache
 * exists is `useSkyLoadProgress`, in hooks/socialdash.
 *
 * The caller must give its container `position: relative` — the overlay is
 * absolutely positioned so it covers the dashboard body while leaving the
 * department tab strip above it reachable.
 */
export const LoadProgressOverlay: React.FC<LoadProgressOverlayProps> = ({ progress }) => {
  const { visible, done, total, lines } = progress

  if (!visible) return null

  // A cycle can be observed before its first query is counted; showing 0/0 as
  // "NaN%" or as a full bar would both be wrong, so an unknown denominator
  // reads as zero progress rather than as complete.
  const percent = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0

  return (
    <Box
      data-testid="sky-load-progress-overlay"
      // Not `aria-busy` on the page: this is a status region, so a screen
      // reader should hear the narration rather than be told to wait.
      role="status"
      aria-live="polite"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: (theme: Theme) => theme.zIndex.modal - 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        // Translucent rather than opaque: the page filling in behind the panel
        // is itself part of the reassurance.
        bgcolor: (theme: Theme) =>
          alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.72 : 0.78),
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        {/* The unfilled track, so the ring reads as a proportion rather than
            as a lone arc floating on the background. */}
        <CircularProgress
          variant="determinate"
          value={100}
          size={72}
          thickness={3.5}
          sx={{ color: (theme: Theme) => alpha(theme.palette.info.main, 0.18) }}
        />
        <CircularProgress
          variant="determinate"
          value={percent}
          size={72}
          thickness={3.5}
          color="info"
          data-testid="sky-load-progress-ring"
          aria-label={`Loading, ${percent}% complete`}
          sx={{ position: 'absolute', left: 0 }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid="sky-load-progress-percent">
            {percent}%
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.04em' }}
        data-testid="sky-load-progress-count"
      >
        {done} of {total} loaded
      </Typography>

      {/* 🔴 FIXED HEIGHT, DELIBERATELY. The log is the part that conveys speed,
          and a block that grows from one line to three as it fills would shift
          the ring above it on every message — turning the reassurance into a
          jitter. The height is reserved from the first frame. */}
      <Box
        data-testid="sky-load-progress-log"
        sx={{
          height: MAX_LINES * LINE_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          overflow: 'hidden',
          minWidth: 240,
        }}
      >
        {lines.slice(0, MAX_LINES).map((line, index) => (
          <Typography
            // Position-keyed on purpose: the list is a scrolling ticker, not a
            // set of identities, and the same text legitimately recurs.
            key={`${index}-${line}`}
            variant="caption"
            noWrap
            sx={{
              height: LINE_HEIGHT,
              lineHeight: `${LINE_HEIGHT}px`,
              color: 'text.secondary',
              // The newest line is the loudest; older ones recede rather than
              // disappearing, so the eye follows the movement downward.
              opacity: index === 0 ? 0.95 : index === 1 ? 0.55 : 0.3,
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  )
}

export default LoadProgressOverlay
